<template>
	<section v-if="bookings.length" class="new-event-transfer-summary">
		<div class="new-event-transfer-summary__heading">
			<span aria-hidden="true"></span>
			<h3>{{translation.resume}}</h3>
		</div>

		<div class="new-event-transfer-summary__body">
			<article
				v-for="(booking, bookingIndex) in bookings"
				:key="`${booking.serviceId}-${bookingIndex}`"
				class="new-event-transfer-summary__item"
			>
				<div class="new-event-transfer-summary__item-heading">
					<strong>{{booking.serviceName}}</strong>
					<div class="new-event-transfer-summary__item-actions">
						<span>{{formatCurrency(booking.price, booking.currency)}}</span>
						<button
							type="button"
							class="new-event-transfer-summary__remove"
							:aria-label="`${translation.detail.remove}: ${booking.serviceName}`"
							@click="$emit('remove-booking', bookingIndex)"
						>
							<span aria-hidden="true">&times;</span>
							{{lowercaseLabel(translation.detail.remove)}}
						</button>
					</div>
				</div>
				<p>
					<strong>{{translation.date}}:</strong>
					<span>{{booking.date}}</span>
				</p>
				<p
					v-for="(guest, guestIndex) in booking.guests"
					:key="`${booking.serviceId}-guest-${guestIndex}`"
				>
					<strong>{{translation.detail.pax}} {{guestIndex + 1}}:</strong>
					<span>{{guest.name}}</span>
				</p>
			</article>
		</div>

		<div class="new-event-transfer-summary__divider"></div>

		<dl class="new-event-transfer-summary__total">
			<dt>Total</dt>
			<dd>{{total}}</dd>
		</dl>

		<button
			type="button"
			class="new-event-transfer-summary__add-another"
			@click="$emit('add-another')"
		>
			<span aria-hidden="true">+</span>
			{{lowercaseLabel(translation.detail.addAnotherTransfer)}}
		</button>
	</section>
</template>

<script>
import { translate } from '../../assets/translations';

export default {
	props: {
		bookings: {
			type: Array,
			default: () => [],
		},
		language: {
			type: Object,
			required: true,
		},
	},
	computed: {
		total() {
			const total = this.bookings.reduce((sum, booking) => sum + this.normalizeCurrencyValue(booking.price), 0);
			const currency = this.bookings[0] ? this.bookings[0].currency : null;
			return this.formatCurrency(total, currency);
		},
		translation() {
			return translate(this.language).transferReservation;
		},
	},
	methods: {
		lowercaseLabel(label) {
			return String(label || '').toLowerCase();
		},
		formatCurrency(value, currency) {
			const symbol = currency && currency.symbol ? currency.symbol : 'R$';
			const number = this.normalizeCurrencyValue(value).toLocaleString('pt-BR', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
			return `${symbol} ${number}`;
		},
		normalizeCurrencyValue(value) {
			if (value === null || value === undefined || value === '') return 0;
			if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

			let normalizedValue = String(value).trim().replace(/[^\d,.-]/g, '');
			if (!normalizedValue) return 0;

			const lastComma = normalizedValue.lastIndexOf(',');
			const lastDot = normalizedValue.lastIndexOf('.');

			if (lastComma !== -1 && lastDot !== -1) {
				normalizedValue = lastComma > lastDot
					? normalizedValue.replace(/\./g, '').replace(',', '.')
					: normalizedValue.replace(/,/g, '');
			} else if (lastComma !== -1) {
				normalizedValue = normalizedValue.replace(',', '.');
			} else if (/^-?\d{1,3}(\.\d{3})+$/.test(normalizedValue)) {
				normalizedValue = normalizedValue.replace(/\./g, '');
			}

			const numberValue = Number(normalizedValue);
			return Number.isFinite(numberValue) ? numberValue : 0;
		},
	},
};
</script>

<style scoped lang="scss">
.new-event-transfer-summary {
	box-sizing: border-box;
	width: 100%;
	color: #5a5a5a;
	font-family: inherit;
}

.new-event-transfer-summary__heading {
	margin-bottom: 52px;
}

.new-event-transfer-summary__heading span {
	margin: 0 0 27px;
	display: block;
	width: 17px;
	height: 17px;
	border-right: 3px solid #66acc6;
	border-bottom: 3px solid #66acc6;
	transform: rotate(45deg) translateY(-5px);
}

.new-event-transfer-summary__heading h3 {
	margin: 0;
	color: #002c41;
	font-size: 18px;
	font-weight: 700;
	line-height: 21px;
}

.new-event-transfer-summary__body {
	display: grid;
	gap: 26px;
	padding: 0 35px;
}

.new-event-transfer-summary__item {
	display: grid;
	gap: 8px;
}

.new-event-transfer-summary__item-heading,
.new-event-transfer-summary__item p,
.new-event-transfer-summary__total {
	display: flex;
	justify-content: space-between;
	gap: 24px;
	margin: 0;
	font-size: 18px;
	line-height: 40px;
}

.new-event-transfer-summary__item-heading > strong,
.new-event-transfer-summary__item p strong {
	font-weight: 700;
}

.new-event-transfer-summary__item-actions > span,
.new-event-transfer-summary__item p span {
	font-weight: 700;
	text-align: right;
}

.new-event-transfer-summary__item-actions {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 20px;
}

.new-event-transfer-summary__remove,
.new-event-transfer-summary__add-another {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	border: 1px solid #d7d7d7;
	border-radius: 10px;
	background: #ffffff;
	color: #5a5a5a;
	font-family: inherit;
	font-size: 14px;
	font-weight: 700;
	line-height: 18px;
	cursor: pointer;
	transition: border-color .15s ease-in-out, color .15s ease-in-out, background-color .15s ease-in-out;
}

.new-event-transfer-summary__remove {
	min-height: 32px;
	padding: 5px 12px;
	gap: 7px;
}

.new-event-transfer-summary__remove span {
	color: #66acc6;
	font-size: 22px;
	font-weight: 400;
	line-height: 16px;
}

.new-event-transfer-summary__remove:hover,
.new-event-transfer-summary__remove:focus-visible {
	border-color: #66acc6;
	color: #002c41;
	outline: none;
}

.new-event-transfer-summary__divider {
	width: 100%;
	height: 1px;
	margin: 26px 0 17px;
	background: rgba(90, 90, 90, .6);
}

.new-event-transfer-summary__total {
	padding: 0 66px 0 35px;
}

.new-event-transfer-summary__total dt,
.new-event-transfer-summary__total dd {
	margin: 0;
}

.new-event-transfer-summary__total dt {
	font-weight: 700;
}

.new-event-transfer-summary__total dd {
	font-weight: 700;
	text-align: right;
}

.new-event-transfer-summary__add-another {
	min-width: 220px;
	min-height: 40px;
	margin: 30px 0 0 35px;
	padding: 8px 18px;
	gap: 10px;
	border-color: #ff9600;
	color: #ff9600;
}

.new-event-transfer-summary__add-another span {
	font-size: 23px;
	font-weight: 400;
	line-height: 18px;
}

.new-event-transfer-summary__add-another:hover,
.new-event-transfer-summary__add-another:focus-visible {
	background: #ff9600;
	color: #ffffff;
	outline: none;
}

@media (max-width: 900px) {
	.new-event-transfer-summary__heading {
		margin-bottom: 30px;
	}

	.new-event-transfer-summary__body,
	.new-event-transfer-summary__total {
		padding: 0;
	}

	.new-event-transfer-summary__item p,
	.new-event-transfer-summary__item-heading,
	.new-event-transfer-summary__total {
		display: grid;
		gap: 4px;
		font-size: 16px;
		line-height: 26px;
	}

	.new-event-transfer-summary__item-actions > span,
	.new-event-transfer-summary__item p span,
	.new-event-transfer-summary__total dd {
		text-align: left;
	}

	.new-event-transfer-summary__item-actions {
		align-items: flex-start;
		justify-content: space-between;
		gap: 12px;
	}

	.new-event-transfer-summary__add-another {
		width: 100%;
		margin: 28px 0 0;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/transfer-reservation/TransfersBookingResume.vue